import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const featureContent = {
  'Vendor CRM': {
    title: 'Vendor CRM',
    headline: 'Stop Chasing Vendors. Start Growing Relationships.',
    subheadline: 'No more scattered spreadsheets, lost emails, or forgetting who was great at your last event. Everything in one place.',
    stats: [
      { value: '5hrs', label: 'saved weekly on vendor management' },
      { value: '40%', label: 'fewer no-shows' },
      { value: '2x', label: 'faster event fill rate' }
    ],
    capabilities: [
      {
        title: 'Never Forget a Great Vendor',
        description: 'Instantly find vendors who crushed it last year. Notes, ratings, and history saved automatically.'
      },
      {
        title: 'One-Click Re-Invites',
        description: 'Fill your next event in minutes by inviting your proven vendors first. No more starting from scratch.'
      },
      {
        title: 'Spot Problems Before They Happen',
        description: 'Flag unreliable vendors, track payment issues, and know who to avoid before they cost you.'
      },
      {
        title: 'Build Your Dream Roster',
        description: 'Tag vendors by specialty, reliability, and crowd favorites. Curate the perfect mix every time.'
      }
    ],
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    )
  },
  'Document Signing': {
    title: 'Document Signing',
    headline: 'Get Signed Docs Without the Back-and-Forth.',
    subheadline: 'Upload your contracts, place signature fields, and vendors sign right inside Pebble. No DocuSign account needed.',
    stats: [
      { value: '90%', label: 'faster than email signing' },
      { value: '0', label: 'unsigned vendors slip through' },
      { value: '100%', label: 'legally binding signatures' }
    ],
    capabilities: [
      {
        title: 'Place Fields on Any PDF',
        description: 'Upload your rules, waivers, or contracts and drag signature and initials fields exactly where you need them.'
      },
      {
        title: 'Vendors Sign In-Platform',
        description: 'Vendors draw their signature or initials directly inside Pebble — no external tools, no broken email chains.'
      },
      {
        title: 'Automatic Reminders',
        description: 'Pebble notifies vendors when docs are ready and sends reminders for anyone who hasn\'t signed yet.'
      },
      {
        title: 'Download Completed PDFs',
        description: 'Every signed document is saved with signatures embedded. Download anytime for your records.'
      }
    ],
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    )
  },
  'Ticketing': {
    title: 'Attendee Ticketing',
    headline: 'Sell Tickets Directly Through Pebble.',
    subheadline: 'Create ticket types, set prices, and let attendees buy in seconds — no third-party tools needed.',
    stats: [
      { value: '∞', label: 'ticket types per event' },
      { value: '0', label: 'monthly minimums or hidden fees' },
      { value: '100%', label: 'of sales tracked in your dashboard' }
    ],
    capabilities: [
      {
        title: 'Create Ticket Types in Minutes',
        description: 'General admission, VIP, early bird — set names, prices, quantities, and sale windows.'
      },
      {
        title: 'Attendees Buy Without an Account',
        description: 'A public ticket page lets anyone buy with just a name and email. No Pebble account required.'
      },
      {
        title: 'Track Sales in Real Time',
        description: 'See who bought tickets, total revenue, and remaining capacity from your event dashboard.'
      },
      {
        title: 'Simple, Transparent Fees',
        description: 'A small per-ticket fee is deducted automatically — no monthly minimums, no hidden charges. Details in FAQs.'
      }
    ],
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
      </svg>
    )
  },
  'Reports': {
    title: 'Reports & Accounting',
    headline: 'Your Finances, Crystal Clear.',
    subheadline: 'See exactly what you earned, export for taxes in one click, and stop dreading bookkeeping season.',
    stats: [
      { value: '1-click', label: 'CSV export for your accountant' },
      { value: '100%', label: 'of your transactions tracked' },
      { value: '0hrs', label: 'spent chasing receipts' }
    ],
    capabilities: [
      {
        title: 'Revenue Summary by Year',
        description: 'Total gross revenue, platform fees, and your net payout — all in one view.'
      },
      {
        title: 'Per-Event Breakdown',
        description: 'See exactly which events drove the most revenue and how many vendors paid.'
      },
      {
        title: 'Tax-Ready CSV Export',
        description: 'Download every transaction with gross, fee, and net columns formatted for QuickBooks, Wave, or your accountant.'
      },
      {
        title: 'Full Transaction History',
        description: 'Search and sort every vendor payment by date, amount, or event. No surprises at tax time.'
      }
    ],
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    )
  },
  'Analytics Dashboard': {
    title: 'Analytics',
    headline: 'Know Exactly What\'s Working. Fix What Isn\'t.',
    subheadline: 'Stop guessing if your events are profitable. See real numbers, spot trends, and make confident decisions.',
    stats: [
      { value: '3hrs', label: 'saved on monthly reports' },
      { value: '25%', label: 'avg revenue increase' },
      { value: '100%', label: 'financial clarity' }
    ],
    capabilities: [
      {
        title: 'See Your Real Profit',
        description: 'Know exactly what you earned after fees, refunds, and expenses. No more spreadsheet headaches.'
      },
      {
        title: 'Find the Leaks',
        description: 'See where vendors drop off in your application process and fix it before your next event.'
      },
      {
        title: 'Impress Sponsors & Partners',
        description: 'Generate professional reports showing attendance, revenue, and growth. Land bigger deals.'
      },
      {
        title: 'Plan Smarter, Not Harder',
        description: 'Compare events side-by-side. Know which venues, dates, and vendor mixes work best for you.'
      }
    ],
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    )
  },
  'Team Seats': {
    title: 'Team Seats',
    headline: 'Run Your Events as a Team, Not a Solo Act.',
    subheadline: 'Add up to 4 contributors so your team can manage vendors, documents, and logistics without sharing your login.',
    stats: [
      { value: '5', label: 'seats included (you + 4 contributors)' },
      { value: '0', label: 'shared passwords needed' },
      { value: '100%', label: 'visibility into who did what' }
    ],
    capabilities: [
      {
        title: 'Delegate Without Losing Control',
        description: 'Give team members access to specific events or your whole account — you stay in charge.'
      },
      {
        title: 'No More Shared Logins',
        description: 'Each contributor gets their own login. Audit logs keep everyone accountable.'
      },
      {
        title: 'Move Faster on Event Day',
        description: 'Let a volunteer handle vendor check-in while you manage the floor. Same account, different devices.'
      },
      {
        title: 'Scales with Your Team',
        description: 'Whether it\'s a business partner, assistant, or intern — add them in seconds and remove them just as fast.'
      }
    ],
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    )
  }
};

const PremiumFeatureGate = ({ featureName, children }) => {
  const { t } = useTranslation();
  const { subscriptionTier } = useAuth();
  const navigate = useNavigate();

  const isPro = subscriptionTier === 'pro' || subscriptionTier === 'premium' || subscriptionTier === 'enterprise';

  if (isPro) {
    return <>{children}</>;
  }

  const content = featureContent[featureName] || featureContent['Vendor CRM'];

  return (
    <div className="w-full bg-grey-50 overflow-auto">
      <div className="flex flex-col">
        <div className="flex-1 flex flex-col lg:flex-row">
          <div className="lg:w-1/2 flex flex-col justify-center px-6 py-16 sm:px-8 sm:py-20 lg:px-16 lg:py-0">
            <div className="max-w-lg">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-xl bg-grey-900 flex items-center justify-center text-white">
                  {content.icon}
                </div>
                <div>
                  <p className="text-xs text-grey-500 tracking-widest uppercase">{t('premiumGate.title')}</p>
                  <p className="text-grey-900 font-medium">{content.title}</p>
                </div>
              </div>

              <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-light text-grey-900 leading-loose mb-4">
                {content.headline}
              </h1>
              
              <p className="text-grey-600 text-lg mb-10 leading-relaxed">
                {content.subheadline}
              </p>

              <div className="grid grid-cols-3 gap-6 mb-10">
                {content.stats.map((stat, index) => (
                  <div key={index} className="text-center lg:text-left">
                    <p className="text-2xl lg:text-3xl font-light text-grey-900 mb-1">{stat.value}</p>
                    <p className="text-xs text-grey-500 leading-tight">{stat.label}</p>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate('/eo/settings?tab=subscription')}
                className="w-full sm:w-auto px-8 py-4 bg-grey-900 text-white font-medium rounded-full hover:bg-grey-800 transition-colors"
              >
                {t('premiumGate.upgradeButton')}
              </button>
              
              <p className="text-xs text-grey-500 mt-4">
                $249/month · Unlimited events · Document signing · Ticketing · Analytics
              </p>
            </div>
          </div>

          <div className="lg:w-1/2 bg-white px-6 py-16 sm:px-8 sm:py-20 lg:px-12 lg:py-16 flex items-center border-l border-grey-200">
            <div className="w-full max-w-md mx-auto space-y-4">
              <p className="text-xs text-grey-500 tracking-widest uppercase mb-6">What You Get</p>
              
              {content.capabilities.map((capability, index) => (
                <div 
                  key={index}
                  className="p-5 rounded-xl bg-grey-50 border border-grey-200 hover:border-grey-300 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-lg bg-grey-900 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-sm font-medium">{index + 1}</span>
                    </div>
                    <div>
                      <h3 className="text-grey-900 font-medium mb-1">{capability.title}</h3>
                      <p className="text-grey-600 text-sm leading-relaxed">{capability.description}</p>
                    </div>
                  </div>
                </div>
              ))}

              <div className="pt-6 border-t border-grey-200 mt-8">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-grey-500">Current plan</span>
                  <span className="text-grey-700">Basic</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-2">
                  <span className="text-grey-500">This feature</span>
                  <span className="text-grey-900 font-medium">Pro only</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-grey-200 px-8 py-4 flex items-center justify-between bg-white">
          <span className="text-xs text-grey-400 tracking-widest">PEBBLE PRO</span>
          <span className="text-xs text-grey-400">Questions? Contact support</span>
        </div>
      </div>
    </div>
  );
};

export default PremiumFeatureGate;
